import { defineModule } from '../../engine/module';

// Platform-admin module UI (docs/modules/admin.md). Rendered by AdminApp inside a nested
// EngineProvider whose client targets /v1/admin, so every screen reuses the same engine
// (ResourceListScreen / ResourceDetailScreen / DataGrid / AutoForm) as the tenant app.
// Screen `path`s are RELATIVE (routes mount under /admin/* in App.tsx); nav `to` and row
// targets are the absolute /admin/… paths. Licensing, announcements and support screens are
// added in later phases.
export const adminModule = defineModule({
  name: 'admin',
  nav: [
    {
      label: 'Tenants',
      order: 0,
      items: [
        { label: 'Tenants', to: '/admin/tenants', icon: 'building', end: true },
        { label: 'Domains', to: '/admin/domains', icon: 'globe' },
      ],
    },
    {
      label: 'Licensing',
      order: 10,
      items: [
        { label: 'Plans', to: '/admin/plans', icon: 'package' },
        { label: 'Subscriptions', to: '/admin/subscriptions', icon: 'credit-card' },
      ],
    },
    {
      label: 'Announcements',
      order: 20,
      items: [{ label: 'Announcements', to: '/admin/announcements', icon: 'message-square', end: true }],
    },
    {
      label: 'Support',
      order: 30,
      items: [
        { label: 'Support inbox', to: '/admin/support', icon: 'life-buoy', end: true },
        { label: 'Access grants', to: '/admin/support-grants', icon: 'shield-check' },
      ],
    },
  ],
  screens: [
    {
      path: 'tenants',
      resource: 'tenants',
      kind: 'list',
      title: 'Tenants',
      subtitle: 'Every organization on the platform',
      columns: ['name', 'slug', 'status', 'is_on_prem', 'created_at'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: 'name' },
      create: false,
      detailPath: '/admin/tenants/:id',
    },
    {
      path: 'tenants/:id',
      resource: 'tenants',
      kind: 'detail',
      // set-status + transfer-ownership render from the resource meta actions.
      tabs: [
        {
          key: 'domains',
          label: 'Domains',
          icon: 'globe',
          resource: 'tenant-domains',
          filter: { tenant_id: ':id' },
          columns: ['domain', 'status', 'verification_method', 'verified_at'],
        },
        {
          key: 'members',
          label: 'Members',
          icon: 'users',
          resource: 'tenant-members',
          filter: { tenant_id: ':id' },
          columns: ['user_email', 'role', 'status', 'created_at'],
        },
        {
          key: 'subscription',
          label: 'Subscription',
          icon: 'credit-card',
          resource: 'tenant-subscriptions',
          filter: { tenant_id: ':id' },
          columns: ['plan_id', 'status', 'seats', 'current_period_end', 'trial_ends_at'],
        },
        {
          key: 'entitlements',
          label: 'Entitlements',
          icon: 'toggle-left',
          resource: 'tenant-entitlements',
          filter: { tenant_id: ':id' },
          columns: ['module_name', 'enabled', 'source'],
        },
      ],
    },
    {
      path: 'domains',
      resource: 'tenant-domains',
      kind: 'list',
      title: 'Domains',
      subtitle: 'Verification status across all tenants',
      columns: ['domain', 'tenant_id', 'status', 'verification_method', 'verified_at'],
      filters: [{ field: 'status', kind: 'chips' }, { field: 'verification_method', kind: 'chips' }],
      query: { sort: 'domain' },
      create: false,
    },

    // ── Licensing ────────────────────────────────────────────────────────
    {
      path: 'plans',
      resource: 'plans',
      kind: 'list',
      title: 'Plans',
      subtitle: 'The subscription plan catalog',
      columns: ['code', 'name', 'price_minor', 'billing_interval', 'trial_days', 'seat_limit', 'is_active', 'is_public'],
      filters: [{ field: 'is_active', kind: 'toggle' }],
      query: { sort: 'sort_order' },
      detailPath: '/admin/plans/:id',
    },
    { path: 'plans/new', resource: 'plans', kind: 'form', title: 'New plan' },
    {
      path: 'plans/:id',
      resource: 'plans',
      kind: 'detail',
      tabs: [
        {
          key: 'modules',
          label: 'Modules',
          icon: 'check-square',
          resource: 'plan-entitlements',
          filter: { plan_id: ':id' },
          columns: ['module_name', 'limit_value'],
        },
      ],
    },
    {
      path: 'subscriptions',
      resource: 'tenant-subscriptions',
      kind: 'list',
      title: 'Subscriptions',
      subtitle: 'Every tenant subscription',
      columns: ['tenant_id', 'plan_id', 'status', 'seats', 'current_period_end', 'trial_ends_at'],
      filters: [{ field: 'status', kind: 'chips' }],
      query: { sort: '-created_at' },
      create: false,
    },

    // ── Announcements ─────────────────────────────────────────────────────
    {
      path: 'announcements',
      resource: 'announcements',
      kind: 'list',
      title: 'Announcements',
      subtitle: 'In-app banners & feature highlights',
      columns: ['title', 'kind', 'tone', 'audience', 'is_published', 'starts_at', 'ends_at', 'created_at'],
      filters: [{ field: 'kind', kind: 'chips' }, { field: 'audience', kind: 'chips' }, { field: 'is_published', kind: 'toggle' }],
      query: { sort: '-created_at' },
      detailPath: '/admin/announcements/:id',
    },
    { path: 'announcements/new', resource: 'announcements', kind: 'form', title: 'New announcement' },
    {
      path: 'announcements/:id',
      resource: 'announcements',
      kind: 'detail',
      // publish / unpublish / notify render from the resource meta actions.
      tabs: [
        {
          key: 'targets',
          label: 'Targets',
          icon: 'target',
          resource: 'announcement-targets',
          filter: { announcement_id: ':id' },
          columns: ['tenant_id', 'plan_id', 'created_at'],
        },
      ],
    },

    // ── Support (cross-tenant feedback triage + conversation) ─────────────
    {
      path: 'support',
      resource: 'feedback',
      kind: 'list',
      title: 'Support',
      subtitle: 'Reports, bugs and requests across every tenant',
      columns: ['tenant_id', 'ref', 'title', 'kind', 'severity', 'status', 'priority', 'vote_count', 'created_at'],
      filters: [{ field: 'status', kind: 'chips' }, { field: 'kind', kind: 'chips' }, { field: 'priority', kind: 'chips' }],
      query: { sort: '-created_at' },
      create: false,
      detailPath: '/admin/support/:id',
    },
    {
      path: 'support/:id',
      resource: 'feedback',
      kind: 'detail',
      // reply (+ internal notes) renders from the resource meta action; triage fields edit inline.
      tabs: [
        {
          key: 'messages',
          label: 'Conversation',
          icon: 'message-square',
          resource: 'feedback-messages',
          filter: { feedback_id: ':id' },
          columns: ['author_kind', 'body', 'is_internal', 'created_at'],
        },
      ],
    },
    {
      path: 'support-grants',
      resource: 'support-access-grants',
      kind: 'list',
      title: 'Support access grants',
      subtitle: 'Time-boxed access tenants have granted the operator',
      columns: ['tenant_id', 'scope', 'reason', 'expires_at', 'revoked_at', 'created_at'],
      query: { sort: '-created_at' },
      create: false,
    },
  ],
});
